// src/Components/careers/jobsData.ts

export interface JobDetailSection {
  heading: string;
  items: string[];
}

export interface JobDetail {
  id: string;
  title: string;
  location: string;
  vacancies: number;
  keySkills: string;
  overviewBullets: string[];
  sections: JobDetailSection[];
}

export const JOBS: JobDetail[] = [
  /* ===================================================== */
  /* 1. Jr ASIC Verification Engineer */
  /* ===================================================== */
  {
    id: "jr-asic-verification-engineer",
    title: "Jr ASIC Verification Engineer",
    location: "Stockholm,Sweden",
    vacancies: 4,
    keySkills: "Digital Circuits, OOP (Java/C++), Linux, Simulation",
    overviewBullets: [
      "Entry-level ASIC Verification role with structured training.",
      "Hands-on exposure to functional hardware verification.",
      "Guided by senior consultants in a project-based environment."
    ],
    sections: [
      {
        heading: "Job Description",
        items: [
          "Participate in a structured training program focused on functional verification.",
          "Work on real project exercises under guidance of senior consultants.",
          "Develop expertise in verification methodologies and tools."
        ]
      },
      {
        heading: "Responsibilities",
        items: [
          "Develop understanding of functional verification domain.",
          "Execute simulations, analyze results, and debug issues.",
          "Support verification infrastructure including scripts and Makefiles.",
          "Document work and participate in reviews.",
          "Collaborate with trainers and team members."
        ]
      },
      {
        heading: "Required Skills & Qualifications",
        items: [
          "Strong fundamentals in digital circuits and hardware design.",
          "Minimum 6 months experience with Java or C++.",
          "Experience with digital circuit simulators.",
          "Linux environment and shell scripting knowledge.",
          "Good communication skills in English.",
          "Bachelor’s degree in Electronics, CS, or related field.",
          "Willingness to work abroad when required."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 2. Sr ASIC Verification Engineer */
  /* ===================================================== */
  {
    id: "sr-asic-verification-engineer",
    title: "Sr ASIC Verification Engineer",
    location: "Stockholm, Sweden (Onsite / Remote Options)",
    vacancies: 1,
    keySkills: "SystemVerilog, UVM, Coverage, ASIC/FPGA Verification",
    overviewBullets: [
      "Senior role verifying complex IP, subsystem, and chip-level designs.",
      "Work on cutting-edge ASIC/FPGA technologies.",
      "Collaborative and quality-driven verification environment."
    ],
    sections: [
      {
        heading: "Responsibilities",
        items: [
          "Develop verification plans and specifications.",
          "Design and maintain SystemVerilog UVM environments.",
          "Implement UVCs and drive coverage closure.",
          "Execute random and directed testing strategies.",
          "Collaborate with design and physical design teams."
        ]
      },
      {
        heading: "Qualifications & Skills",
        items: [
          "Master’s degree in Electrical or Computer Engineering.",
          "5–7+ years ASIC/FPGA verification experience.",
          "Expertise in SystemVerilog and UVM.",
          "Strong analytical and problem-solving skills.",
          "Excellent communication and teamwork abilities."
        ]
      },
      {
        heading: "Why Join Us",
        items: [
          "Work on advanced semiconductor designs.",
          "Collaborative, cross-functional teams.",
          "Opportunity to influence verification methodologies."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 3. Sr ASIC Designer */
  /* ===================================================== */
  {
    id: "sr-asic-designer",
    title: "Sr ASIC Designer",
    location: "Stockholm, Sweden (Onsite – Min 3 Days/Week)",
    vacancies: 1,
    keySkills: "RTL, Microarchitecture, ASIC/SoC, Low Power",
    overviewBullets: [
      "Lead end-to-end ASIC/SoC development.",
      "Own architecture, RTL, and system integration.",
      "Mentor engineers and drive technical strategy."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Lead ASIC/SoC design from specification to tape-out.",
          "Develop RTL in Verilog/SystemVerilog.",
          "Define chip-level architecture and interfaces.",
          "Support verification and physical design teams.",
          "Drive synthesis, timing closure, and low-power techniques."
        ]
      },
      {
        heading: "Qualifications",
        items: [
          "10–15+ years ASIC/SoC design experience.",
          "Multiple full-cycle tape-out ownerships.",
          "Expertise in RTL, STA, low-power, and DFT concepts.",
          "Strong leadership and mentoring skills."
        ]
      },
      {
        heading: "Bonus Skills",
        items: [
          "AI/ML accelerators or networking experience.",
          "Chiplet or advanced packaging knowledge.",
          "Post-silicon validation or FPGA prototyping."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 4. Sr FPGA Designer */
  /* ===================================================== */
  {
    id: "sr-fpga-designer",
    title: "Sr FPGA Designer",
    location: "Edinburgh, UK (Hybrid)",
    vacancies: 1,
    keySkills: "FPGA, VHDL, Altium, High-Speed Digital",
    overviewBullets: [
      "Full-lifecycle FPGA design role.",
      "Work on high-speed digital systems.",
      "Hybrid work and collaborative teams."
    ],
    sections: [
      {
        heading: "Job Responsibilities",
        items: [
          "Develop FPGA logic and VHDL.",
          "Design Altium schematics and PCB layouts.",
          "Work with communication protocols.",
          "Support EMC testing and manufacturing."
        ]
      },
      {
        heading: "Required Skills",
        items: [
          "FPGA/VHDL experience.",
          "Altium schematic and PCB design.",
          "High-speed digital systems knowledge.",
          "Strong problem-solving and teamwork."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 5. Sr ASIC Physical Design Engineer */
  /* ===================================================== */
  {
    id: "sr-asic-physical-design-engineer",
    title: "Sr ASIC Physical Design Engineer",
    location: "Europe",
    vacancies: 1,
    keySkills: "P&R, STA, CTS, IR Drop",
    overviewBullets: [
      "Drive physical implementation of advanced ASICs.",
      "Optimize power, performance, and area.",
      "Work on leading-edge technology nodes."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Execute full-chip floorplanning and P&R.",
          "Perform CTS, STA, IR drop, and signal integrity.",
          "Collaborate to meet aggressive PPA targets."
        ]
      },
      {
        heading: "Required Qualifications",
        items: [
          "4–10 years ASIC physical design experience.",
          "Experience on 5nm, 7nm, 10nm, or 14nm nodes.",
          "Proficiency with Synopsys, Cadence, and Mentor tools."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 6. Hardware Technical Lead */
  /* ===================================================== */
  {
    id: "hardware-technical-lead",
    title: "Hardware Technical Lead",
    location: "Europe",
    vacancies: 1,
    keySkills: "Hardware Leadership, PCB, Compliance Standards",
    overviewBullets: [
      "Lead multi-project hardware engineering teams.",
      "End-to-end ownership of hardware delivery.",
      "Strategic and technical leadership role."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Manage teams of 8–10 engineers.",
          "Oversee hardware delivery lifecycle.",
          "Ensure compliance with industry standards.",
          "Mentor, hire, and improve engineering processes."
        ]
      },
      {
        heading: "Required Qualifications",
        items: [
          "10+ years hardware development experience.",
          "3+ years in technical leadership.",
          "Expertise in industrial hardware and compliance standards."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 7. SAP FI/CO Consultant */
  /* ===================================================== */
  {
    id: "sap-fico-consultant",
    title: "SAP FI/CO Consultant",
    location: "Europe",
    vacancies: 1,
    keySkills: "SAP FI/CO, S/4HANA, Finance",
    overviewBullets: [
      "Senior SAP FI/CO consulting role.",
      "End-to-end SAP implementation projects.",
      "Customer-facing enterprise consulting."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Analyze business requirements and configure SAP FI/CO.",
          "Conduct workshops and support UAT.",
          "Provide post-go-live support and training."
        ]
      },
      {
        heading: "Required Skills & Experience",
        items: [
          "8+ years SAP FI/CO experience.",
          "Strong S/4HANA and ECC knowledge.",
          "Excellent communication and problem-solving skills."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 8. Senior SAP ABAP Developer */
  /* ===================================================== */
  {
    id: "senior-sap-abap-developer",
    title: "Senior SAP ABAP Developer",
    location: "Europe",
    vacancies: 1,
    keySkills: "ABAP OO, CDS, OData, S/4HANA",
    overviewBullets: [
      "Senior ABAP role in S/4HANA environments.",
      "Design scalable enterprise SAP solutions.",
      "Work closely with functional teams."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Develop ABAP OO solutions in S/4HANA.",
          "Integrate SAP systems via IDoc, BAPI, RFC.",
          "Perform performance tuning and code reviews."
        ]
      },
      {
        heading: "Required Skills & Experience",
        items: [
          "8+ years ABAP experience.",
          "4+ years S/4HANA experience.",
          "Strong knowledge of CDS and OData."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 9. SAP VIM Consultant */
  /* ===================================================== */
  {
    id: "sap-vim-consultant",
    title: "SAP VIM Consultant (OpenText)",
    location: "Europe",
    vacancies: 1,
    keySkills: "OpenText VIM, SAP FI/MM, S/4HANA",
    overviewBullets: [
      "Invoice automation and SAP VIM specialist role.",
      "Work with European finance and compliance.",
      "S/4HANA migration and optimization projects."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Implement OpenText VIM solutions.",
          "Integrate with SAP FI/MM and OCR tools.",
          "Support migrations, upgrades, and UAT."
        ]
      },
      {
        heading: "Required Skills & Experience",
        items: [
          "8+ years SAP VIM experience.",
          "Strong OpenText VIM expertise.",
          "Knowledge of PEPPOL and European regulations."
        ]
      }
    ]
  },

  /* ===================================================== */
  /* 10. Senior SAP S/4HANA Consultant */
  /* ===================================================== */
  {
    id: "senior-sap-s4hana-consultant",
    title: "Senior SAP S/4HANA Consultant",
    location: "Europe",
    vacancies: 1,
    keySkills: "SAP S/4HANA, FI/CO/MM/SD",
    overviewBullets: [
      "Senior functional consultant role.",
      "S/4HANA implementations and migrations.",
      "Enterprise transformation projects."
    ],
    sections: [
      {
        heading: "Key Responsibilities",
        items: [
          "Translate business requirements into SAP solutions.",
          "Design and configure SAP S/4HANA modules.",
          "Support testing, training, and go-live."
        ]
      },
      {
        heading: "Required Skills & Experience",
        items: [
          "8+ years SAP consulting experience.",
          "Deep expertise in one or more SAP modules.",
          "Strong analytical and communication skills."
        ]
      }
    ]
  }
];

/* Utility */
export const getJobById = (id?: string) =>
  JOBS.find((job) => job.id === id);
